<template>
	<div v-bind="$attrs">
		<h1 v-if="$slots.h1" :style="{'transform': `translateY(${parallax}px)`, 'opacity': opacity}">
			<slot name="h1" />
		</h1>
		<h2 v-if="$slots.h2" :style="{'transform': `translateY(${parallax}px)`, 'opacity': opacity}">
			<slot name="h2" />
		</h2>
		<aside v-if="$slots.menu">
			<slot name="menu" />
		</aside>
	</div>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'

const props = defineProps({
	sticky: {
		type: Boolean,
		default: false,
	},
	speed: {
		type: Number,
		default: 0.5,
	},
})

const scrollTop = ref(0)
const windowHeight = ref(0)
const halfScreen = ref(0)

const parallax = computed(() =>
{
	if( props.sticky )
	{
		return 0
	}

	if( scrollTop.value < windowHeight.value )
	{
		return scrollTop.value * props.speed
	}

	return 0
})

const opacity = computed(() =>
{
	if( props.sticky )
	{
		return 1
	}

	if( scrollTop.value >= halfScreen.value && halfScreen.value > 0 )
	{
		return Math.max(0, 1 - (scrollTop.value - halfScreen.value) / halfScreen.value)
	}

	return 1
})

if( !props.sticky )
{
	useEventListener(document, 'scroll', () =>
	{
		window.requestAnimationFrame(() =>
		{
			scrollTop.value = document.documentElement.scrollTop
		})
	})
}

onMounted(() =>
{
	windowHeight.value = window.innerHeight
	halfScreen.value = windowHeight.value * 0.35
})
</script>

<style lang="css" scoped>
div
{
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	gap: 2rem;
	width: 100%;
	height: 100svh;
	cursor: default;
	z-index: 2;
}

div h1
{
	width: 100%;
	padding: 0 2rem;
	box-sizing: border-box;
	font-family: "brother-1816", sans-serif;
	text-align: center;
	font-size: 5rem;
	margin: 0;
	font-weight: 200;
	text-shadow: 0 0 20px #fff;
	color: rgb(var(--white));
	will-change: transform, opacity;
}

div h2
{
	width: 100%;
	padding: 0 2rem;
	box-sizing: border-box;
	font-family: "brother-1816", sans-serif;
	text-align: center;
	font-size: 2.5rem;
	margin: 0;
	font-weight: 200;
	color: rgb(var(--white));
	will-change: transform, opacity;
}

aside
{
	display: flex;
	gap: 1.75rem;
	margin-top: 1.25rem;
}

aside :deep(a)
{
	display: inline-block;
	padding: 1rem 2rem;
	font-weight: 300;
	background-color: rgb(var(--blue));
	color: rgb(var(--white));
	text-decoration: none;
	font-family: "brother-1816", sans-serif;
	text-transform: uppercase;
	letter-spacing: .2rem;
	border-radius: 2.5rem;
	transition: background-color .3s ease;
}

aside :deep(a:hover)
{
	background-color: rgb(var(--blue-dark));
}

@media screen and (max-width: 900px)
{
	div h1
	{
		font-size: 3.5rem;
	}

	div h2
	{
		font-size: 1.75rem;
	}
}

@media screen and (max-width: 550px)
{
	div h1
	{
		font-size: 3rem;
	}

	div h2
	{
		font-size: 1.5rem;
	}
}
</style>